import React from "react";
import { motion } from "framer-motion";
import { Layers, Phone, FileCheck2, UsersRound } from "lucide-react";

const pillars = [
  {
    icon: Layers,
    color: "#3b82f6",
    title: "We unify your entire stack",
    text: "Your 10+ softwares become one operating system — scheduling, comms, imaging, claims, payments, analytics. Staff stops jumping between tools; everything lives in one place.",
  },
  {
    icon: Phone,
    color: "#6366f1",
    title: "We put AI in your front office",
    text: "AI runs inbound & outbound calls, scheduling, case acceptance, faxes, and patient messaging — the entire front office on autopilot.",
  },
  {
    icon: FileCheck2,
    color: "#8b5cf6",
    title: "We put AI in your back office",
    text: "AI calls insurance for benefits, submits claims on its own, posts EOBs, and works denials — the full revenue cycle, end-to-end.",
  },
  {
    icon: UsersRound,
    color: "#d946ef",
    title: "We embed the experts who make it work",
    text: "A dedicated account manager meets with you multiple times a week, and consulting is bundled in. Most practices buy AI, never configure it, and turn it off — we embed the experts so yours actually succeeds.",
  },
];

const PartnershipSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1240px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-10 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.08]"
          >
            How we partner with{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
              your office.
            </span>
          </motion.h2>
        </div>

        {/* 4 cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 + i * 0.1, duration: 0.5 }}
                className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.1)] p-5 flex flex-col border-t-[4px]"
                style={{ borderTopColor: p.color }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${p.color}15`, color: p.color }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <span className="text-[11px] font-bold text-slate-300 tracking-widest tabular-nums">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-[15px] font-bold text-slate-900 leading-tight mb-2">
                  {p.title}
                </h3>
                <p className="text-[12.5px] text-slate-600 leading-[1.55]">
                  {p.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PartnershipSlide;
